import React, { useEffect, useState, useCallback } from "react";
import LoadingComponent from "commons/loading/Loading.component";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { getImageUrl } from "helpers/Image.helper";
import { getFlagByCountryCode, getStudentNickname } from "helpers/String.helper";
import { getCastingSummary } from "api/school/v2/casting.api";
import { usePopup } from "contexts/PopupContext";
import StudentInfoPopupContainer from "commons/student/popup/StudentInfoPopup.container";

const Casting = () => {
    const { showPopup } = usePopup();

    interface ICastingSummary {
        totalFavoriteCount: number;

        totalChattingCount: number;
        waitingChattingCount: number;
        canceledChattingCount: number;
        deniedChattingCount: number;
        onChattingCount: number;
        closedChattingCount: number;

        totalViewCount: number;
        todayViewCount: number;
        weeklyViewCount: number;
        monthlyViewCount: number;
        
        traineeList?: Array<any>;
    }

    //* 캐스팅 요약페이지 조회
    const [isLoading, setIsLoading] = useState(true);
    const [info, setInfo] = useState<ICastingSummary | null>(null);

    useEffect(() => {
        //* 캐스팅 요약페이지 조회
        const fetchCastingSummary = async () => {
            try {
                const data = await getCastingSummary();
                if (data?.info) {
                    setInfo(data.info);
                }
            } catch (error) {
                console.error('Failed to fetch casting summary:', error);
            } finally {
                setIsLoading(false);
            }
        }
        fetchCastingSummary();
    }, []);

    const handleShowStudentInfoPopup = useCallback((studentId: number) => {
        showPopup(<StudentInfoPopupContainer
            studentId={studentId} />);
    }, []);

    return (
        <>
            <main className="bg-gray-50 min-h-screen">
                <>
                    {isLoading ? (
                        <LoadingComponent />
                    ) : (
                        <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                            <h2 className="text-3xl font-bold text-gray-900 mb-8">
                                {getLanguageValue(PAGE_TYPE.SCHOOL, "castingSummary.txtTitle")}
                            </h2>

                            <div className="space-y-6">
                                {/* 내 캐스팅 정보 섹션 */}
                                <section className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4 sm:mb-6">
                                    <div className="p-4 sm:p-6 border-b border-gray-100">
                                        <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                                            {getLanguageValue(PAGE_TYPE.SCHOOL, "castingSummary.txtCastingSectionTitle")}
                                        </h2>
                                    </div>

                                    <div className="p-3 sm:p-4 md:p-6">
                                        <div className="space-y-4 sm:space-y-6">
                                            {/* 대화 섹션 */}
                                            <div>
                                                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                                    {/* 대기중인 요청 */}
                                                    <div className="bg-gray-50 p-3 sm:p-4 rounded-lg sm:rounded-xl 
                                                    hover:bg-gray-100 transition-all duration-200 
                                                    transform hover:-translate-y-1 shadow-sm hover:shadow">
                                                        <div className="flex flex-row sm:flex-col h-full justify-between sm:justify-start">
                                                            <h3 className="text-xs sm:text-sm font-medium text-gray-500 
                                                            mb-0 sm:mb-2 mr-2 sm:mr-0">
                                                                {getLanguageValue(PAGE_TYPE.SCHOOL, "castingSummary.txtCastingSectionWaitingRequest")}
                                                            </h3>
                                                            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-[#CF3688]">
                                                                {info?.waitingChattingCount?.toLocaleString() || "N/A"}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* 진행중인 대화 */}
                                                    <div className="bg-gray-50 p-3 sm:p-4 rounded-lg sm:rounded-xl 
                                                    hover:bg-gray-100 transition-all duration-200 
                                                    transform hover:-translate-y-1 shadow-sm hover:shadow">
                                                        <div className="flex flex-row sm:flex-col h-full justify-between sm:justify-start">
                                                            <h3 className="text-xs sm:text-sm font-medium text-gray-500 
                                                            mb-0 sm:mb-2 mr-2 sm:mr-0">
                                                                {getLanguageValue(PAGE_TYPE.SCHOOL, "castingSummary.txtCastingSectionChatting")}
                                                            </h3>
                                                            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-[#CF3688]">
                                                                {info?.onChattingCount?.toLocaleString() || "N/A"}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* 종료된 채팅 */}
                                                    <div className="bg-gray-50 p-3 sm:p-4 rounded-lg sm:rounded-xl 
                                                    hover:bg-gray-100 transition-all duration-200 
                                                    transform hover:-translate-y-1 shadow-sm hover:shadow">
                                                        <div className="flex flex-row sm:flex-col h-full justify-between sm:justify-start">
                                                            <h3 className="text-xs sm:text-sm font-medium text-gray-500 
                                                            mb-0 sm:mb-2 mr-2 sm:mr-0">
                                                                {getLanguageValue(PAGE_TYPE.SCHOOL, "castingSummary.txtCastingSectionClosedChat")}
                                                            </h3>
                                                            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-[#CF3688]">
                                                                {info?.closedChattingCount?.toLocaleString() || "N/A"}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* 취소된 요청 */}
                                                    <div className="bg-gray-50 p-3 sm:p-4 rounded-lg sm:rounded-xl 
                                                    hover:bg-gray-100 transition-all duration-200 
                                                    transform hover:-translate-y-1 shadow-sm hover:shadow">
                                                        <div className="flex flex-row sm:flex-col h-full justify-between sm:justify-start">
                                                            <h3 className="text-xs sm:text-sm font-medium text-gray-500 
                                                            mb-0 sm:mb-2 mr-2 sm:mr-0">
                                                                {getLanguageValue(PAGE_TYPE.SCHOOL, "castingSummary.txtCastingSectionCanceledRequest")}
                                                            </h3>
                                                            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-[#CF3688]">
                                                                {info?.canceledChattingCount?.toLocaleString() || "N/A"}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* 거절한 요청 */}
                                                    <div className="bg-gray-50 p-3 sm:p-4 rounded-lg sm:rounded-xl 
                                                    hover:bg-gray-100 transition-all duration-200 
                                                    transform hover:-translate-y-1 shadow-sm hover:shadow">
                                                        <div className="flex flex-row sm:flex-col h-full justify-between sm:justify-start">
                                                            <h3 className="text-xs sm:text-sm font-medium text-gray-500 
                                                            mb-0 sm:mb-2 mr-2 sm:mr-0">
                                                                {getLanguageValue(PAGE_TYPE.SCHOOL, "castingSummary.txtCastingSectionDeniedRequest")}
                                                            </h3>
                                                            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-[#CF3688]">
                                                                {info?.deniedChattingCount?.toLocaleString() || "N/A"}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* 총 채팅 */}
                                                    <div className="bg-gray-50 p-3 sm:p-4 rounded-lg sm:rounded-xl 
                                                    hover:bg-gray-100 transition-all duration-200 
                                                    transform hover:-translate-y-1 shadow-sm hover:shadow">
                                                        <div className="flex flex-row sm:flex-col h-full justify-between sm:justify-start">
                                                            <h3 className="text-xs sm:text-sm font-medium text-gray-500 
                                                            mb-0 sm:mb-2 mr-2 sm:mr-0">
                                                                {getLanguageValue(PAGE_TYPE.SCHOOL, "castingSummary.txtCastingSectionTotalChat")}
                                                            </h3>
                                                            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-[#CF3688]">
                                                                {info?.totalChattingCount?.toLocaleString() || "N/A"}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                {/* 조회수 섹션 */}
                                <section className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4 sm:mb-6">
                                    <div className="p-4 sm:p-6 border-b border-gray-100">
                                        <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                                            조회수 통계
                                        </h2>
                                    </div>

                                    <div className="p-3 sm:p-4 md:p-6">
                                        <div className="space-y-4 sm:space-y-6">
                                            <div>
                                                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                                                    {/* 오늘 조회수 */}
                                                    <div className="bg-blue-50 p-3 sm:p-4 rounded-lg sm:rounded-xl 
                                                    hover:bg-blue-100 transition-all duration-200 
                                                    transform hover:-translate-y-1 shadow-sm hover:shadow">
                                                        <div className="flex flex-row sm:flex-col h-full justify-between sm:justify-start">
                                                            <h3 className="text-xs sm:text-sm font-medium text-blue-600 
                                                            mb-0 sm:mb-2 mr-2 sm:mr-0">
                                                                오늘
                                                            </h3>
                                                            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-600">
                                                                {info?.todayViewCount?.toLocaleString() || "N/A"}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* 이번 주 조회수 */}
                                                    <div className="bg-blue-50 p-3 sm:p-4 rounded-lg sm:rounded-xl 
                                                    hover:bg-blue-100 transition-all duration-200 
                                                    transform hover:-translate-y-1 shadow-sm hover:shadow">
                                                        <div className="flex flex-row sm:flex-col h-full justify-between sm:justify-start">
                                                            <h3 className="text-xs sm:text-sm font-medium text-blue-600 
                                                            mb-0 sm:mb-2 mr-2 sm:mr-0">
                                                                이번 주
                                                            </h3>
                                                            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-600">
                                                                {info?.weeklyViewCount?.toLocaleString() || "N/A"}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* 이번 달 조회수 */}
                                                    <div className="bg-blue-50 p-3 sm:p-4 rounded-lg sm:rounded-xl 
                                                    hover:bg-blue-100 transition-all duration-200 
                                                    transform hover:-translate-y-1 shadow-sm hover:shadow">
                                                        <div className="flex flex-row sm:flex-col h-full justify-between sm:justify-start">
                                                            <h3 className="text-xs sm:text-sm font-medium text-blue-600 
                                                            mb-0 sm:mb-2 mr-2 sm:mr-0">
                                                                이번 달
                                                            </h3>
                                                            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-600">
                                                                {info?.monthlyViewCount?.toLocaleString() || "N/A"}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* 총 조회수 */}
                                                    <div className="bg-blue-50 p-3 sm:p-4 rounded-lg sm:rounded-xl 
                                                    hover:bg-blue-100 transition-all duration-200 
                                                    transform hover:-translate-y-1 shadow-sm hover:shadow">
                                                        <div className="flex flex-row sm:flex-col h-full justify-between sm:justify-start">
                                                            <h3 className="text-xs sm:text-sm font-medium text-blue-600 
                                                            mb-0 sm:mb-2 mr-2 sm:mr-0">
                                                                총 조회수
                                                            </h3>
                                                            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-600">
                                                                {info?.totalViewCount?.toLocaleString() || "N/A"}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                {/* 좋아요 섹션 */}
                                <section className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4 sm:mb-6">
                                    <div className="p-4 sm:p-6 border-b border-gray-100">
                                        <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                                            좋아요 통계
                                        </h2>
                                    </div>

                                    <div className="p-3 sm:p-4 md:p-6">
                                        <div className="space-y-4 sm:space-y-6">
                                            <div>
                                                <div className="grid grid-cols-1 gap-3 sm:gap-4">
                                                    {/* 총 좋아요 */}
                                                    <div className="bg-pink-50 p-3 sm:p-4 rounded-lg sm:rounded-xl 
                                                    hover:bg-pink-100 transition-all duration-200 
                                                    transform hover:-translate-y-1 shadow-sm hover:shadow">
                                                        <div className="flex flex-row sm:flex-col h-full justify-between sm:justify-start">
                                                            <h3 className="text-xs sm:text-sm font-medium text-pink-600 
                                                            mb-0 sm:mb-2 mr-2 sm:mr-0">
                                                                총 좋아요
                                                            </h3>
                                                            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-pink-600">
                                                                {info?.totalFavoriteCount?.toLocaleString() || "N/A"}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                {/* 대화 요청을 받은 지망생 목록 */}
                                {info?.traineeList && info.traineeList.length > 0 && (
                                    <section className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4 sm:mb-6">
                                        <div className="p-4 sm:p-6 border-b border-gray-100">
                                            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                                                {getLanguageValue(PAGE_TYPE.SCHOOL, "castingSummary.txtTraineeListTitle")}
                                                ({info.traineeList.length})
                                            </h2>
                                        </div>
                                        <div className="p-4 sm:p-6">
                                            <div className="overflow-x-auto">
                                                <div className="flex gap-1">
                                                    {info.traineeList.map((student: any) => (
                                                        <div key={student.id} className="w-[100px] sm:w-[125px] lg:w-[150px] flex-shrink-0 cursor-pointer"
                                                            onClick={() => handleShowStudentInfoPopup(student?.id)}
                                                        >
                                                            <div className="flex flex-col items-center">
                                                                <div className="rounded-full overflow-hidden w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 border-2 border-gray-100 relative">
                                                                    <img
                                                                        src={getImageUrl(student?.profileImageInfo)}
                                                                        alt={getStudentNickname(student)}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
                                                                        {getFlagByCountryCode(student?.countryInfo?.code)}
                                                                    </div>
                                                                </div>
                                                                <div className="mt-2 text-center w-full">
                                                                    <p className="text-gray-900 text-xs truncate px-2">
                                                                        {getStudentNickname(student)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                )}
                            </div>
                        </div>
                    )}
                </>
            </main>
        </>
    );
};

export default Casting; 